import { GanttStatic } from '../../../../assets/js/codebase/dhtmlxgantt';
import { GanttTask } from './gantt.types';

/** Bound scale calculations, not the task store, when zooming into a long history. */
export class GanttTimelineRange {
    private fullStart?: Date;
    private fullEnd?: Date;
    private center?: Date;
    private pendingAnchor?: Date;
    private scaleKey = '';
    private limited = false;
    private adjusting = false;
    private managed = false;
    private configuredStart?: Date;
    private configuredEnd?: Date;
    private configuredOutside = false;
    private scrollFrame?: number;

    constructor(private gantt: GanttStatic) {
        gantt.attachEvent('onBeforeGanttRender', () => {
            this.updateRange();
            return true;
        });
        gantt.attachEvent('onGanttScroll', () => this.moveWindowAtEdge());
        gantt.attachEvent('onBeforeTaskSelected', id => {
            const task = gantt.getTask(id);
            if (this.limited && task.start_date && task.end_date &&
                (task.end_date < gantt.config.start_date! || task.start_date > gantt.config.end_date!)) {
                this.showDate(task.start_date);
            }
            return true;
        });
        gantt.attachEvent('onAfterTaskUpdate', (_id, task) => this.extendRange(task));
        gantt.attachEvent('onAfterTaskAdd', (_id, task) => this.extendRange(task));
        gantt.attachEvent('onAfterTaskAutoSchedule', task => this.extendRange(task));
    }

    public destroy(): void {
        if (this.scrollFrame !== undefined) cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = undefined;
    }

    public setTasks(tasks: GanttTask[], start?: Date, end?: Date, center?: Date, showOutside?: boolean): void {
        if (this.managed) {
            this.gantt.config.start_date = start;
            this.gantt.config.end_date = end;
            this.gantt.config.show_tasks_outside_timescale = showOutside ?? this.configuredOutside;
            this.managed = false;
        }
        this.destroy();
        this.configuredStart = start;
        this.configuredEnd = end;
        let first = Infinity;
        let last = -Infinity;
        for (const task of tasks) {
            if (task.start_date instanceof Date) first = Math.min(first, task.start_date.getTime());
            if (task.end_date instanceof Date) last = Math.max(last, task.end_date.getTime());
        }
        this.fullStart = start ?? (Number.isFinite(first) ? new Date(first - 86400000) : undefined);
        this.fullEnd = end ?? (Number.isFinite(last) ? new Date(last + 86400000) : undefined);
        this.center = center ?? this.readCenter() ?? this.fullStart;
        this.updateRange();
    }

    public anchor(date: Date | undefined): void { this.destroy(); this.pendingAnchor = date; }

    public showDate(date: Date): void {
        this.destroy();
        this.pendingAnchor = undefined;
        this.center = date;
        if (!this.updateRange()) return;
        const scroll = this.gantt.getScrollState();
        this.adjusting = true;
        try {
            this.gantt.render();
            this.gantt.scrollTo(Math.max(0, this.gantt.posFromDate(date) - this.gantt.$task.clientWidth / 2), scroll.y);
        } finally {
            this.adjusting = false;
        }
    }

    private readCenter(): Date | undefined {
        if (!this.gantt.$task || !this.gantt.getScale()) return;
        return this.gantt.dateFromPos(this.gantt.getScrollState().x + this.gantt.$task.clientWidth / 2) ?? undefined;
    }

    private updateRange(): boolean {
        if (!this.fullStart || !this.fullEnd || this.fullEnd <= this.fullStart) return false;
        const scales = this.gantt.config.scales;
        const scale = scales?.[scales.length - 1];
        if (!scale) return false;
        const key = scales.map(item => item.unit + ':' + (item.step ?? 1)).join('/');
        if (this.pendingAnchor) this.center = this.pendingAnchor;
        else if (key !== this.scaleKey) this.center = this.readCenter() ?? this.center;
        this.pendingAnchor = undefined;
        this.scaleKey = key;
        const center = this.center ?? this.fullStart;
        const columnMs = this.gantt.date.add(center, scale.step ?? 1, scale.unit).getTime() - center.getTime();
        if (columnMs <= 0) return false;
        const span = this.fullEnd.getTime() - this.fullStart.getTime();
        const windowMs = Math.min(span, columnMs * (columnMs < 86400000 ? 4000 : 10000));
        this.limited = span > windowMs;
        if (!this.limited) {
            if (!this.managed) return false;
            this.gantt.config.start_date = this.configuredStart;
            this.gantt.config.end_date = this.configuredEnd;
            this.gantt.config.show_tasks_outside_timescale = this.configuredOutside;
            this.managed = false;
            return true;
        }
        const startMs = this.limited
            ? Math.max(this.fullStart.getTime(), Math.min(center.getTime() - windowMs / 2, this.fullEnd.getTime() - windowMs))
            : this.fullStart.getTime();
        const endMs = startMs + windowMs;
        if (!this.managed) this.configuredOutside = this.gantt.config.show_tasks_outside_timescale;
        // The grid remains a navigator for every loaded task, including dates
        // outside this fine-scale window. Only the timeline calculation is bounded.
        this.gantt.config.show_tasks_outside_timescale = true;
        this.managed = true;
        if (this.gantt.config.start_date?.getTime() === startMs && this.gantt.config.end_date?.getTime() === endMs) return false;
        this.gantt.config.start_date = new Date(startMs);
        this.gantt.config.end_date = new Date(endMs);
        this.managed = true;
        return true;
    }

    private moveWindowAtEdge(): void {
        if (this.adjusting || this.scrollFrame !== undefined || !this.limited || !this.gantt.$task || !this.fullStart || !this.fullEnd) return;
        const width = this.gantt.$task.clientWidth;
        const scroll = this.gantt.getScrollState();
        const right = this.gantt.posFromDate(this.gantt.config.end_date!) - width;
        const atLeft = scroll.x < width && this.gantt.config.start_date! > this.fullStart;
        const atRight = scroll.x > right - width && this.gantt.config.end_date! < this.fullEnd;
        if (!atLeft && !atRight) return;
        const date = this.readCenter();
        if (!date) return;
        // The native scrollbar is still inside its scroll handler here. Wait
        // until it releases its re-entry guard before restoring the date position.
        this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = undefined;
            if (!this.gantt.$root) return;
            this.adjusting = true;
            try {
                this.center = date;
                if (this.updateRange()) {
                    this.gantt.render();
                    this.gantt.scrollTo(Math.max(0, this.gantt.posFromDate(date) - width / 2), scroll.y);
                }
            } finally {
                this.adjusting = false;
            }
        });
    }

    private extendRange(task: { start_date?: Date; end_date?: Date }): void {
        if (this.fullStart && task.start_date && task.start_date < this.fullStart) this.fullStart = new Date(task.start_date);
        if (this.fullEnd && task.end_date && task.end_date > this.fullEnd) this.fullEnd = new Date(task.end_date);
    }
}
